
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TradingCentralNewsArticleInfo struct
 */
class TradingCentralNewsArticleInfo {
  /**
   * Initialize struct
   * @param {!string=} id
   * @param {!string=} provider
   * @param {!string=} publishDate
   * @param {!string=} title
   * @constructor
   */
  constructor (id = '', provider = '', publishDate = '', title = '') {
    this.id = id
    this.provider = provider
    this.publishDate = publishDate
    this.title = title
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TradingCentralNewsArticleInfo}
   * @param {!TradingCentralNewsArticleInfo} other Other struct
   * @returns {!TradingCentralNewsArticleInfo} This struct
   */
  copy (other) {
    if (other.id != null) {
      this.id = other.id
    } else {
      this.id = null
    }
    if (other.provider != null) {
      this.provider = other.provider
    } else {
      this.provider = null
    }
    if (other.publishDate != null) {
      this.publishDate = other.publishDate
    } else {
      this.publishDate = null
    }
    if (other.title != null) {
      this.title = other.title
    } else {
      this.title = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TradingCentralNewsArticleInfo}
   * @returns {!TradingCentralNewsArticleInfo} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TradingCentralNewsArticleInfoModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TradingCentralNewsArticleInfoModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TradingCentralNewsArticleInfo}
   * @param {!TradingCentralNewsArticleInfo} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TradingCentralNewsArticleInfo)) {
      throw new TypeError('Instance of TradingCentralNewsArticleInfo is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TradingCentralNewsArticleInfo}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      id: ((this.id != null) ? this.id : null),
      provider: ((this.provider != null) ? this.provider : null),
      publishDate: ((this.publishDate != null) ? this.publishDate : null),
      title: ((this.title != null) ? this.title : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TradingCentralNewsArticleInfo.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TradingCentralNewsArticleInfo} other Object value
   * @returns {!TradingCentralNewsArticleInfo} Created struct
   */
  static fromObject (other) {
    return new TradingCentralNewsArticleInfo().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TradingCentralNewsArticleInfo}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TradingCentralNewsArticleInfo.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TradingCentralNewsArticleInfo}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10009
  }
}

export { TradingCentralNewsArticleInfo };

/**
 * Fast Binary Encoding TradingCentralNewsArticleInfo field model
 */
class FieldModelTradingCentralNewsArticleInfo extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.id = new fbe.FieldModelString(buffer, 4 + 4)
    this.provider = new fbe.FieldModelString(buffer, this.id.fbeOffset + this.id.fbeSize)
    this.publishDate = new fbe.FieldModelString(buffer, this.provider.fbeOffset + this.provider.fbeSize)
    this.title = new fbe.FieldModelString(buffer, this.publishDate.fbeOffset + this.publishDate.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.id.fbeSize + this.provider.fbeSize + this.publishDate.fbeSize + this.title.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.id.fbeExtra + this.provider.fbeExtra + this.publishDate.fbeExtra + this.title.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTradingCentralNewsArticleInfo.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10009
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.id.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.provider.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.provider.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.provider.fbeSize

    if ((fbeCurrentSize + this.publishDate.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.publishDate.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.publishDate.fbeSize

    if ((fbeCurrentSize + this.title.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.title.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.title.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @param {!TradingCentralNewsArticleInfo} fbeValue Default value, defaults is new TradingCentralNewsArticleInfo()
   * @returns {!TradingCentralNewsArticleInfo} TradingCentralNewsArticleInfo value
   */
  get (fbeValue = new TradingCentralNewsArticleInfo()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @param {!TradingCentralNewsArticleInfo} fbeValue TradingCentralNewsArticleInfo value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) <= fbeStructSize) {
      fbeValue.id = this.id.get()
    } else {
      fbeValue.id = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.provider.fbeSize) <= fbeStructSize) {
      fbeValue.provider = this.provider.get()
    } else {
      fbeValue.provider = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.provider.fbeSize

    if ((fbeCurrentSize + this.publishDate.fbeSize) <= fbeStructSize) {
      fbeValue.publishDate = this.publishDate.get()
    } else {
      fbeValue.publishDate = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.publishDate.fbeSize

    if ((fbeCurrentSize + this.title.fbeSize) <= fbeStructSize) {
      fbeValue.title = this.title.get()
    } else {
      fbeValue.title = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.title.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @param {!TradingCentralNewsArticleInfo} fbeValue TradingCentralNewsArticleInfo value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @param {TradingCentralNewsArticleInfo} fbeValue TradingCentralNewsArticleInfo value
   */
  setFields (fbeValue) {
    this.id.set(fbeValue.id)
    this.provider.set(fbeValue.provider)
    this.publishDate.set(fbeValue.publishDate)
    this.title.set(fbeValue.title)
  }
}

export { FieldModelTradingCentralNewsArticleInfo };

/**
 * Fast Binary Encoding TradingCentralNewsArticleInfo model
 */
class TradingCentralNewsArticleInfoModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTradingCentralNewsArticleInfo(this.buffer, 4)
  }

  /**
   * Get the TradingCentralNewsArticleInfo model
   * @this {!TradingCentralNewsArticleInfoModel}
   * @returns {!FieldModelTradingCentralNewsArticleInfo} model TradingCentralNewsArticleInfo model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TradingCentralNewsArticleInfoModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TradingCentralNewsArticleInfoModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TradingCentralNewsArticleInfoModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TradingCentralNewsArticleInfoModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTradingCentralNewsArticleInfo.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TradingCentralNewsArticleInfoModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TradingCentralNewsArticleInfoModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TradingCentralNewsArticleInfoModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TradingCentralNewsArticleInfoModel}
   * @param {!TradingCentralNewsArticleInfo} value TradingCentralNewsArticleInfo value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TradingCentralNewsArticleInfoModel}
   * @param {!TradingCentralNewsArticleInfo} value TradingCentralNewsArticleInfo value, defaults is new TradingCentralNewsArticleInfo()
   * @return {!object} Deserialized TradingCentralNewsArticleInfo value and its size
   */
  deserialize (value = new TradingCentralNewsArticleInfo()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TradingCentralNewsArticleInfo(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TradingCentralNewsArticleInfo(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TradingCentralNewsArticleInfoModel}
   * @param {!number} prev Previous TradingCentralNewsArticleInfo model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TradingCentralNewsArticleInfoModel };
